// Making a league.
//
// OUT OF `LeagueSetupScreen` on 2026-09-17, where it was a `fetch` with a
// hand-written envelope cast and a `status !== 201` check. Two things are
// better for the move, beyond the grep:
//
// THE IDEMPOTENCY KEY IS NOW SENT. `POST /api/leagues` accepts one and has
// since Phase 5; nothing was sending it, so the guarantee existed and did
// nothing. The screen's `creating` flag stops a second TAP, which is a real
// defense and the only one that was here — but it does not stop a retry after
// a timeout, and a phone on a bad connection is exactly where somebody ends
// up with two leagues and no way to tell which one their friends were invited
// to. The key is minted once per attempt and reused if the same attempt is
// made again.
//
// THE KEY IS PER ATTEMPT, NOT PER SCREEN. Somebody who genuinely wants two
// leagues with the same answers must be able to have them, so the key is
// minted where the button is pressed rather than held for the life of the
// wizard. `newAttemptKey()` is exported for that: the screen keeps it for one
// attempt, and drops it once the attempt has a definite answer.

import { LeagueRef, type CreateLeagueInput } from '@fsp/domain';
import { send, type Reply } from './api';

/**
 * A fresh idempotency key.
 *
 * `crypto.randomUUID` is present in every browser this app supports and in
 * the dev server; the fallback is there because it is absent over plain HTTP
 * on some older Androids, and a missing key must degrade to "no idempotency"
 * rather than to a crash on the button that makes a league.
 */
export function newAttemptKey(): string {
  try {
    return crypto.randomUUID();
  } catch {
    // THE ONE EXEMPTION FROM `no-restricted-properties` IN THIS LAYER, and it
    // is visible on purpose (the rule arrived 2026-09-20; see eslint.config.js).
    // This is not a view reading the clock, which is what the rule is for — it
    // is a last-resort unique-enough string on a browser with no
    // `crypto.randomUUID`, used once, sent once, and never compared to
    // anything. Asking the clock port for it would make the button that
    // creates a league depend on the league API being reachable.
    // eslint-disable-next-line no-restricted-properties
    return `k-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
  }
}

export function createLeague(
  input: CreateLeagueInput,
  attemptKey: string,
): Promise<Reply<LeagueRef>> {
  return send('POST', '/leagues', input, LeagueRef, {
    headers: { 'idempotency-key': attemptKey },
  });
}
